import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { positionNames } from '@sorare/core/src/lib/players';

import type { Options } from '#src/lib/performance.tsx';
import {
  ALL_COMPETITIONS_ITEM,
  ALL_TEAMS_ITEM,
  FilterKey,
  homeAwayOptions,
} from '#src/lib/performance.tsx';

import type {
  useGetFiltersOptions_anyCard,
  useGetFiltersOptions_anyPlayer,
  useGetFiltersOptions_playerGameScoreInterface,
} from './__generated__/useGetFiltersOptions.graphql.ts';

export const useGetFiltersOptions = <
  T extends useGetFiltersOptions_playerGameScoreInterface,
  P extends useGetFiltersOptions_anyPlayer,
  C extends useGetFiltersOptions_anyCard,
>({
  so5Scores,
  player,
  card,
}: {
  so5Scores: T[];
  player: Nullable<P>;
  card: Nullable<C>;
}): Options => {
  const teams = so5Scores.reduce(
    (acc, score) => {
      const { anyTeam } = score.anyPlayerGameStats;
      if (!anyTeam) return acc;
      if (!acc.find(t => t.value === anyTeam.slug)) {
        acc.push({
          value: anyTeam.slug,
          label: (
            <LabelM bold>
              <Horizontal gap={1} as="span">
                <TeamAvatar size={16} team={anyTeam} />
                {anyTeam.officialName}
              </Horizontal>
            </LabelM>
          ),
        });
      }
      return acc;
    },
    [ALL_TEAMS_ITEM]
  );

  const competitions = so5Scores.reduce(
    (acc, score) => {
      const { anyGame } = score.anyPlayerGameStats;
      if (!anyGame) return acc;
      const { competition } = anyGame;
      if (!acc.find(t => t.value === competition.slug)) {
        acc.push({
          value: competition.slug,
          label: <LabelM bold>{competition.displayName}</LabelM>,
        });
      }
      return acc;
    },
    [ALL_COMPETITIONS_ITEM]
  );

  const positions = card
    ? []
    : (player?.sport === Sport.FOOTBALL &&
        player?.cardPositions.map(position => ({
          value: position,
          label: (
            <LabelM bold>
              <FormattedMessage {...positionNames[position]} />
            </LabelM>
          ),
        }))) ||
      [];

  return {
    [FilterKey.TEAM]: teams,
    [FilterKey.COMPETITION]: competitions,
    [FilterKey.HOME_AWAY]: homeAwayOptions,
    [FilterKey.POSITION]: positions,
    [FilterKey.MODE]: [],
  };
};

useGetFiltersOptions.fragments = {
  playerGameScoreInterface: gql`
    fragment useGetFiltersOptions_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      anyPlayerGameStats {
        id
        anyTeam {
          slug
          officialName
          ...TeamAvatar_team
        }
        anyGame {
          id
          competition {
            slug
            displayName
          }
          homeTeam {
            slug
            ...TeamAvatar_team
          }
          awayTeam {
            slug
            ...TeamAvatar_team
          }
        }
      }
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<useGetFiltersOptions_playerGameScoreInterface>,
  anyPlayer: gql`
    fragment useGetFiltersOptions_anyPlayer on AnyPlayerInterface {
      slug
      cardPositions
      sport
    }
  ` as TypedDocumentNode<useGetFiltersOptions_anyPlayer>,
  anyCard: gql`
    fragment useGetFiltersOptions_anyCard on AnyCardInterface {
      slug
    }
  ` as TypedDocumentNode<useGetFiltersOptions_anyCard>,
};
